import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import DraggableEditorOption from './common/DraggableEditorOption';
import { useAppSelector } from '@/redux/hooks';
import { selectPackagingOptions } from '@/redux/selectors/packagingSelectors';
import { DRAGGABLE_TYPES } from '@/redux/slices/packagingSlice.types';
import { Box, Checkbox, FilledInput, FormControlLabel, Grid, lighten, Typography } from '@mui/material';
import { useDroppable } from '@dnd-kit/core';
import PolarisIcon from '../_common/PolarisIcon';

interface ToolboxOptionsProps {
  isInline?: boolean;
}

interface SearchState {
  search: string;
  segments: boolean;
  overlays: boolean;
}

const ToolboxOptions = ({ isInline = false }: ToolboxOptionsProps) => {
  const options = useAppSelector(selectPackagingOptions);
  const { setNodeRef: setToolboxDroppableRef } = useDroppable({
    id: 'toolbox-droppable'
  });
  const [searchState, setSearchState] = useState<SearchState>({
    search: '',
    segments: true,
    overlays: true
  });
  const optionsList = useMemo(() => {
    return options.filter((option) => {
      if (searchState.segments && searchState.overlays) {
        return option.label.toLowerCase().includes(searchState.search.toLowerCase());
      } else if (searchState.segments) {
        return option.segmentType === DRAGGABLE_TYPES.SEGMENT && option.label.toLowerCase().includes(searchState.search.toLowerCase());
      } else if (searchState.overlays) {
        return option.segmentType === DRAGGABLE_TYPES.OVERLAY && option.label.toLowerCase().includes(searchState.search.toLowerCase());
      }
      return false;
    });
  }, [searchState, options]);

  const handleChange = (key: keyof SearchState, value: SearchState[keyof SearchState]) => {
    setSearchState({
      ...searchState,
      [key]: value
    });
  };

  return (
    <Content>
      <Header>
        <Typography variant="h5">Cut Types</Typography>
        <FormWrapper>
          <StyledFilledInput
            sx={{ height: 37, minWidth: 150 }}
            // eslint-disable-next-line jsx-a11y/no-autofocus -- intentional: focuses search input when toolbox options panel opens
            autoFocus
            id="search-bar"
            autoComplete="off"
            placeholder="Search Cut Types"
            value={searchState.search}
            onChange={(e) => setSearchState({ ...searchState, search: e.target.value })}
            endAdornment={<PolarisIcon icon="search" height="20" width="20" />}
          />
          <Box display="flex" justifyContent="space-between">
            <FormControlLabel
              control={<Checkbox checked={searchState.segments} onChange={(e) => handleChange('segments', e.target.checked)} name="segments" />}
              label="Cut Types"
            />
            <FormControlLabel
              control={<Checkbox checked={searchState.overlays} onChange={(e) => handleChange('overlays', e.target.checked)} name="overlays" />}
              label="Overlays"
            />
          </Box>
        </FormWrapper>
      </Header>
      <div style={{ height: isInline ? '100%' : 'calc(100% - 110px)', overflowY: 'scroll' }}>
        <StyledGrid container ref={setToolboxDroppableRef}>
          {optionsList.map((option) => (
            <DraggableEditorOption key={option.draggableId} option={option} />
          ))}
        </StyledGrid>
      </div>
    </Content>
  );
};

export default ToolboxOptions;

const Content = styled.div`
  height: calc(100% - 30px);
  background: ${({ theme }) => theme.palette.background.paper};
  padding: ${({ theme }) => theme.spacing(2, 4)};
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing(1, 0, 2, 0)};
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  column-gap: ${({ theme }) => theme.spacing(2)};

  & .MuiAutocomplete-root {
    padding-top: 0px;
  }

  & .MuiFilledInput-root {
    padding-top: 0px;
  }

  & .MuiInputBase-root {
    height: 36px;
    border-radius: 50px;
    padding-top: 0px;
    width: 330px;
  }

  & .MuiAutocomplete-popupIndicator {
    transform: none;
    color: ${({ theme }) => lighten(theme.palette.background.paper, 0.5)};
  }
`;

const StyledGrid = styled(Grid)`
  display: flex;
  flex-direction: row;
  overflow-y: scroll;
  padding-bottom: ${({ theme }) => theme.spacing(1)};
`;

const StyledFilledInput = styled(FilledInput)`
  border: 0;
  border-radius: 50px;
  transition: ease opacity 2000ms;

  input {
    padding: ${({ theme }) => theme.spacing(1.25, 2)};

    &:-webkit-autofill {
      transition: background-color 50000s ease-in-out 0s !important;
      -webkit-box-shadow: 0 0 0 0px transparent inset !important;
    }
  }

  &::placeholder {
    opacity: 0.7;
  }
  &:after,
  &:before {
    display: none;
    border: 0;
  }
`;
